<template>
  <div class="station-picker">
    <!-- Layout split: lista sinistra, mappa destra -->
    <div class="picker-layout">

      <!-- Colonna lista -->
      <div class="picker-list">
        <div v-if="nearestStation" class="nearest-hint">
          <AeIcon name="Navigation" :size="14" />
          {{ t('stationPicker.nearest') }}: <strong>{{ nearestStation.name }}</strong> ({{ nearestStation._distKm }} km)
        </div>

        <div class="station-list">
          <div
            v-for="s in sortedStations"
            :key="s.id_station"
            class="station-item"
            :class="{
              selected: modelValue === s.id_station,
              nearest: s.id_station === nearestStation?.id_station
            }"
            @click="$emit('update:modelValue', s.id_station)"
          >
            <div class="station-item-info">
              <div class="station-item-main">
                <span class="station-name">{{ s.name }} </span>
                <span v-if="s._distKm !== null" class="station-dist">{{ s._distKm }} km</span>
              </div>
              <div class="station-quality">
                <span :class="`quality-${stationQuality(s).level}`">
                  {{ stationQuality(s).message }}
                </span>
              </div>
            </div>
            <AeIcon v-if="s.id_station === nearestStation?.id_station" name="Star" :size="13" class="icon-star" />
            <AeIcon v-if="modelValue === s.id_station" name="Check" :size="15" class="icon-check" />
          </div>

          <div v-if="sortedStations.length === 0" class="no-stations">
            {{ t('stationPicker.noStationsAvailable') }}
          </div>
        </div>
      </div>

      <!-- Colonna mappa -->
      <div class="picker-map">
        <AeMap
          v-if="mapLayers.length > 0"
          :opt="mapOptions"
          :layers="mapLayers"
          @feature-click="onMarkerClick"
        >
          <template #legend>
            <div class="sp-legend">
              <div class="sp-legend-item">
                <span class="sp-dot" style="background:#e53e3e"></span> {{ t('stationPicker.farmCenter') }}
              </div>
              <div class="sp-legend-item">
                <span class="sp-dot" style="background:#0047AB"></span> {{ t('stationPicker.station') }}
              </div>
            </div>
          </template>
        </AeMap>
        <div v-else class="map-placeholder">
          <AeIcon name="Map" :size="32" />
          <p>{{ t('stationPicker.noFarmCenterCoordinates') }}</p>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import L from 'leaflet'
import { AeMap, AeIcon } from '@aevue/components'
import { baseLayers } from '../composables/useMapLayers.js'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    default: null
  },      // id_station selezionato
  stations: { type: Array, default: () => [] },     // lista stazioni (id_station, name, lat, lon)
  farmCenter: { type: Object, default: null }       // { lat, lon, name }
})

const emit = defineEmits(['update:modelValue'])

function _safeZoomMarker(marker) {
  const orig = marker._animateZoom
  if (orig && !orig._safe) {
    marker._animateZoom = function (e) {
      if (!this._map) return
      orig.call(this, e)
    }
    marker._animateZoom._safe = true
  }
  return marker
}

function daysDiff(d) {
  return (Date.now() - new Date(d).getTime()) / 86400000
}

function stationQuality(s) {
  const q = s.quality || {}
  const dailyDays  = q.last_daily  ? daysDiff(q.last_daily)  : Infinity
  const hourlyDays = q.last_hourly ? daysDiff(q.last_hourly) : Infinity

  const dailyOk  = dailyDays  <= 1
  const hourlyOk = hourlyDays <= 1

  if (dailyOk && hourlyOk) return { level: 'ok', message: t('stationPicker.dataUpdated') }

  // Ritardo accettabile (< 5 gg): messaggio verde
  const dailyRecent  = dailyDays  < 5
  const hourlyRecent = hourlyDays < 5

  if (dailyRecent && hourlyRecent) {
    const parts = []
    if (dailyOk)   parts.push(t('stationPicker.dailyDataUpdated'))
    else           parts.push(t('stationPicker.dailyDataAsOf', { date: q.last_daily }))
    if (hourlyOk)  parts.push(t('stationPicker.hourlyDataUpdated'))
    else           parts.push(t('stationPicker.hourlyDataAsOf', { date: q.last_hourly }))
    return { level: 'warn', message: parts.join(' · ') }
  }

  const problems = []
  if (!q.last_daily)       problems.push(t('stationPicker.dailyDataMissing'))
  else if (!dailyRecent)   problems.push(t('stationPicker.dailyDataAsOf', { date: q.last_daily }))
  if (!q.last_hourly)      problems.push(t('stationPicker.hourlyDataMissing'))
  else if (!hourlyRecent)  problems.push(t('stationPicker.hourlyDataAsOf', { date: q.last_hourly }))

  return { level: 'bad', message: problems.join(' · ') }
}

// ── Distanza Haversine (km) ───────────────────────────────────────────────────

function distKm(lat1, lon1, lat2, lon2) {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLon = ((lon2 - lon1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)))
}

// ── Stazioni ordinate per distanza ───────────────────────────────────────────

const MAX_DIST_KM = 50

const sortedStations = computed(() => {
  const fc = props.farmCenter
  return props.stations
    .map(s => ({ ...s, _distKm: distKm(fc?.lat, fc?.lon, s.lat, s.lon) }))
    .filter(s => s._distKm === null || s._distKm <= MAX_DIST_KM)
    .sort((a, b) => {
      if (a._distKm === null) return 1
      if (b._distKm === null) return -1
      return a._distKm - b._distKm
    })
})

const nearestStation = computed(() => sortedStations.value[0] || null)

// ── Mappa ─────────────────────────────────────────────────────────────────────

const mapCenter = computed(() => {
  const fc = props.farmCenter
  if (fc?.lat && fc?.lon) return [fc.lat, fc.lon]
  // fallback: centroide delle stazioni
  const valid = props.stations.filter(s => s.lat && s.lon)
  if (valid.length === 0) return [42.5, 12.5]
  const avgLat = valid.reduce((s, x) => s + x.lat, 0) / valid.length
  const avgLon = valid.reduce((s, x) => s + x.lon, 0) / valid.length
  return [avgLat, avgLon]
})


const mapOptions = computed(() => ({
  zoom: 10,
  height: window.innerWidth <= 640 ? '200px' : '500px',
  baseLayers,
  layer_selector: false,
  center: {"lat": mapCenter .value[0], "lon": mapCenter.value[1] }
}))

const mapLayers = computed(() => {
  const layers = []

  // Layer stazioni (solo quelle entro 50km)
  const stationFeatures = sortedStations.value
    .filter(s => s.lat && s.lon)
    .map(s => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
      properties: {
        id: s.id_station,
        name: s.name,
        nearest: s.id_station === nearestStation.value?.id_station,
        status: s.id_station === props.modelValue
          ? 'selected'
          : s.id_station === nearestStation.value?.id_station
          ? 'nearest'
          : 'normal'
      }
    }))

  if (stationFeatures.length > 0) {
    layers.push({
      name: 'Stazioni',
      no_fit_bounds: true,
      type: 'geojson',
      data: { type: 'FeatureCollection', features: stationFeatures },
      options: {
        point_to_circle: true,
        circleOptions: { radius: 7, fillOpacity: 0.9, color: '#fff', weight: 2 },
        theme: {
          type: 'categorical',
          enabled: true,
          field: 'status',
          values: [
            { value: 'selected', fillColor: '#3182ce', radius: 10 },
            { value: 'nearest', fillColor: '#38a169', radius: 7 },
            { value: 'normal', fillColor: '#718096', radius: 7 },
          ]
        },
        popup: true,
        popupTemplate: (p) => `<strong>${p.name}</strong>${p.nearest ? `<br><em>⭐ ${t('stationPicker.nearest')}</em>` : ''}`
      }
    })
  }

  // Layer azienda
  const fc = props.farmCenter
  if (fc?.lat && fc?.lon) {
    layers.push({
      name: 'Azienda',
      no_fit_bounds: true,
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [fc.lon, fc.lat] },
          properties: { name: fc.name || t('stationPicker.farmCenter') }
        }]
      },
      options: {
        keep_original: true,
        pointToLayer: (_feature, latlng) => {
          const m = L.marker(latlng, {
            icon: L.divIcon({
              className: '',
              html: `<div style="
                width:16px;height:16px;
                background:#e53e3e;
                border:2px solid #fff;
                border-radius:50%;
                box-shadow:0 1px 4px rgba(0,0,0,.4)
              "></div>`,
              iconSize: [16, 16],
              iconAnchor: [8, 8]
            })
          })
          return _safeZoomMarker(m)
        },
        popup: true,
        popupTemplate: (p) => `<strong>🏠 ${p.name}</strong>`
      }
    })
  }

  // Layer selezione — cerchio dorato sulla stazione scelta
  const selected = sortedStations.value.find(s => s.id_station === props.modelValue)
  if (selected?.lat && selected?.lon) {
    layers.push({
      name: 'Selezione',
      no_fit_bounds: true,
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [selected.lon, selected.lat] },
          properties: {}
        }]
      },
      options: {
        keep_original: true,
        point_to_circle: true,
        circleOptions: { radius: 14, fillOpacity: 0 },
        style: { fillColor: 'transparent', color: '#FFD700', weight: 3, fillOpacity: 0, opacity: 1 },
      }
    })
  }

  return layers
})

function onMarkerClick(feature) {
  const id = feature?.properties?.id
  if (id) {
    emit('update:modelValue', id)
  }
}
</script>

<style scoped>
.station-picker {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.picker-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1rem;
  min-height: 500px;
}

/* Lista */
.picker-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.nearest-hint {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  color: #276749;
  background: rgba(47, 133, 90, 0.08);
  border: 1px solid rgba(47, 133, 90, 0.2);
  border-radius: var(--border-radius, 8px);
  padding: 0.45rem 0.7rem;
  flex-shrink: 0;
}

.station-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  overflow-y: auto;
  max-height: 450px;
  padding-right: 2px;
}

.station-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: var(--border-radius, 8px);
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  flex-shrink: 0;
}

.station-item:hover {
  background: rgba(66, 153, 225, 0.06);
  border-color: var(--primary-color, #4299e1);
}

.station-item.selected {
  background: rgba(66, 153, 225, 0.1);
  border-color: var(--primary-color, #4299e1);
}

.station-item.nearest:not(.selected) {
  border-color: rgba(47, 133, 90, 0.35);
}

.station-item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.station-item-main {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.station-quality {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-muted, #718096);
}

.quality-ok   { color: #38a169; }
.quality-warn { color: #2b9348; }
.quality-bad  { color: #e53e3e; }

.station-name {
  font-weight: 500;
  font-size: 0.88rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.station-dist {
  font-size: 0.78rem;
  color: var(--text-muted, #718096);
  white-space: nowrap;
  flex-shrink: 0;
}

.icon-star { color: #d69e2e; flex-shrink: 0; }
.icon-check { color: var(--primary-color, #4299e1); flex-shrink: 0; }

.no-stations {
  font-size: 0.85rem;
  color: var(--text-muted, #a0aec0);
  padding: 1rem 0;
  text-align: center;
}

/* Legenda mappa */
.sp-legend {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: var(--text-color, #2d3748);
}

.sp-legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
}

.sp-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.2);
  flex-shrink: 0;
}

/* Mappa */
.picker-map {
  border-radius: var(--border-radius, 8px);
  overflow: hidden;
  border: 1px solid var(--border-color, #e2e8f0);
}

.map-placeholder {
  height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: var(--text-muted, #a0aec0);
  background: var(--surface-color, #f7f7f7);
}

@media (max-width: 640px) {
  .picker-layout {
    grid-template-columns: 1fr;
    min-height: unset;
  }

  .station-list {
    max-height: 150px;
  }

  .picker-map {
    height: 200px;
  }

  .picker-map :deep(.ae-map),
  .picker-map :deep(.leaflet-container) {
    height: 200px !important;
  }
}
</style>
